import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import { useSelector } from 'react-redux';
import { el } from 'date-fns/locale';
import './Notification.css';
import { getInitialAvatarJSX } from '../../../../utils/stringUtils';


// -------------------------------------------------------------------
//  Color / Label mapping (kept from original)
// -------------------------------------------------------------------
const VARIANT_STYLES = {
    success: { badgeBg: '#DEFCD9', iconColor: '#479A32' },
    info: { badgeBg: '#FFF', iconColor: '#181D27' },
    error: { badgeBg: '#FEE4E2', iconColor: '#D92D20' },
};

// -------------------------------------------------------------------
//  Helpers
// -------------------------------------------------------------------
/** Converts "circle-check" or "circleCheck" or "CircleCheck" → "CircleCheck" */
function toPascalCase(str) {
    return str
        .replace(/(^\w|-\w)/g, (m) => m.replace('-', '').toUpperCase());
}


// -------------------------------------------------------------------
//  Icon renderer
// -------------------------------------------------------------------
function NotificationIcon({ icon, variant }) {
    if (!icon) return null;

    const isInfo = variant === 'info';
    const { badgeBg, iconColor } = VARIANT_STYLES[variant] ?? VARIANT_STYLES.info;
    const containerClass = `notification-icon-container${isInfo ? ' notification-icon-container_info' : ''}`;

    // ── Google Material Symbols ──────────────────────────────────────
    if (icon.type === 'google') {
        return (
            <span className={containerClass} style={{ background: badgeBg }}>
                <span
                    className="material-symbols-outlined notification-icon-google"
                    style={{
                        color: iconColor,
                        fontSize: icon.size ?? 16,
                        fontVariationSettings: icon.fontVariationSettings
                            ?? "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 16",
                        lineHeight: 1,
                        userSelect: 'none',
                    }}
                >
                    {icon.name}
                </span>
            </span>
        );
    }

    // ── Lucide React ─────────────────────────────────────────────────
    if (icon.type === 'lucide') {
        const componentName = toPascalCase(icon.name);
        const LucideIcon = LucideIcons[componentName];

        if (!LucideIcon) {
            console.warn(
                `[Notification] Unknown Lucide icon: "${icon.name}" (resolved to "${componentName}"). ` +
                `Check https://lucide.dev/icons for valid names.`
            );
            return null;
        }

        return (
            <span className={containerClass} style={{ background: badgeBg }}>
                <LucideIcon
                    size={icon.size ?? 16}
                    color={iconColor}
                    strokeWidth={icon.strokeWidth ?? 1.5}
                    className="notification-icon-lucide"
                    aria-hidden="true"
                />
            </span>
        );
    }

    // ── Image / URL fallback ─────────────────────────────────────────
    const src = icon.url || '/fallback-icon.png';
    return (
        <span className={containerClass} style={{ background: badgeBg }}>
            <img src={src} className="notification-icon" alt="" />
        </span>
    );
}

// -------------------------------------------------------------------
//  Unified Notification Item
// -------------------------------------------------------------------
export default function Notification({ notification, onDismiss, onReply }) {
    const { t } = useTranslation();
    const audioRef = useRef(null);
    const { selectedSpeaker } = useSelector((state) => state.mediaControls);
    const selectedSpeakerRef = useRef(selectedSpeaker);

    // Keep selectedSpeakerRef in sync
    useEffect(() => {
        selectedSpeakerRef.current = selectedSpeaker;
    }, [selectedSpeaker]);

    // --------------- Dismiss handler ---------------
    const handleDismiss = () => {
        onDismiss?.(notification.id);
    };

    // --------------- Reply handler ---------------
    const handleReply = () => {
        if (!notification.conversation.id) {
            console.error('Attempted to call notification reply callback with no conversation id.')
            return;
        }
        onReply(notification.conversation.id)
    }
    // --------------- Auto close timer on mount ---------------
    useEffect(() => {
        if (notification.options?.permanent) return;
        const timer =
            setTimeout(() => {
                handleDismiss();
            }, notification?.options?.ttl ?? 8000);
        return () => clearTimeout(timer);
    }, [onDismiss, notification, notification?.message?.text]);

    // --------------- Sound playback on mount ---------------
    useEffect(() => {
        if (notification?.sound?.url) {
            const audio = new Audio(notification.sound.url);
            audio.volume = notification.sound.volume ?? 1.0;
            if (selectedSpeakerRef.current?.deviceId && audio.setSinkId) {
                audio.setSinkId(selectedSpeakerRef.current.deviceId).then(() => {
                    audioRef.current = audio;
                    audio.play().catch(() => { });
                })
            }
            else {
                audioRef.current = audio;
                audio.play().catch(() => { });
            }
        }
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, [notification?.sound?.url, notification?.sound?.volume, notification?.message?.text]);

    // --------------- Translation helpers ---------------
    const translateText = (textObj) => {
        if (!textObj) return '';
        if (textObj.translate) {
            return t(textObj.text, textObj.variables ?? {});
        }
        return textObj.text;
    };

    const headerText = translateText(notification.header);
    const messageText = notification.message ? translateText(notification.message) : null;



    // --------------- Message-type layout ---------------
     if (notification.type === 'message') {
        const { user, conversation, options } = notification;
        const roomName = conversation?.name || options?.room_name; // kept for legacy, not used in translation

        return (
            <div className="notification-message-container">
                {/* Avatar + online dot */}
                <div className="notification-message-avatar-wrapper">
                    {(user?.urlphoto && user?.urlphoto !== 'Avatar.png') ?
                        (<img
                            src={user?.urlphoto}
                            alt={user?.firstName + " " + user?.lastName}
                            className="notification-message-avatar"
                        />)
                        :
                        (getInitialAvatarJSX(user?.firstName, user?.lastName, { className: "notification-message-avatar" }))}
                    {user?.is_online && <span className="notification-message-online-dot" />}
                </div>

                {/* Body */}
                <div className="notification-message-body">
                    <p className="notification-message-text">
                        <span className="notification-message-sender">{user?.firstName + " " + user?.lastName}</span>
                        {' '}{messageText}
                    </p>

                    <div className="notification-message-actions">
                        <button
                            type="button"
                            className="notification-message-btn notification-message-btn--ignore"
                            onClick={handleDismiss}
                        >
                            {t('ignore')}
                        </button>
                        <button
                            type="button"
                            className="notification-message-btn notification-message-btn--reply"
                            onClick={() => { handleReply() }}
                        >
                            {t('REPLY')}
                        </button>
                    </div>
                </div>

                {/* Dismiss cross */}
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label={t('close_notification')}
                    className="notification-close-btn"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>

                    { conversation?.unread_count > 1 &&
                        <span className='notification-message-unread-count'>
                            {conversation.unread_count + " " + t("new")}
                        </span>
                    }
            </div>
        );
    }

    // --------------- Standard layout (success / info / error) ---------------
    return (
        <div className="notification-container">
            <NotificationIcon icon={notification.icon} variant={notification.variant} />

            <div>
                {headerText && <div className="notification-header">{headerText}</div>}
                {messageText && <div className="notification-text">{messageText}</div>}
            </div>

            <button
                type="button"
                onClick={handleDismiss}
                aria-label={t('close_notification')}
                className="notification-close-btn"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
        </div>
    );
}
